import { Injectable, InjectionToken, inject } from '@angular/core';
import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { logErrorToSentry } from '@libs/core/monitoring';
import { AuthService } from '@libs/core/auth-pt';

const BYPASS_ERROR_INTERCEPTOR = new HttpContextToken<boolean>(() => false);
export function bypassErrorInterceptorNotifier() {
  return new HttpContext().set(BYPASS_ERROR_INTERCEPTOR, true);
}
const ERROR_INTERCEPTOR_REQUEST_TYPE = new HttpContextToken<ErrorInterceptorRequestType>(() => 'generic');
type ErrorInterceptorRequestType = 'get' | 'create' | 'update' | 'delete' | 'generic';
export function interceptorRequestType(requestType: ErrorInterceptorRequestType) {
  return new HttpContext().set(ERROR_INTERCEPTOR_REQUEST_TYPE, requestType);
}

export const NOTIFICATION_SERVICE = new InjectionToken<NotificationServiceInterface>('NOTIFICATION_SERVICE');

interface NotificationServiceInterface {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
}
@Injectable()
export class ApiErrorsInterceptor implements HttpInterceptor {
  private notificationService = inject<NotificationServiceInterface>(NOTIFICATION_SERVICE);
  private authService = inject(AuthService);

  private isCarrierDisabledError(error: HttpErrorResponse): boolean {
    return typeof error.error === 'string' && error.error === 'CARRIER_DISABLED';
  }

  private extractBackendMessage(error: HttpErrorResponse): string | null {
    const backendError = error.error;

    if (!backendError) {
      return null;
    }

    if (typeof backendError === 'string') {
      try {
        const parsed = JSON.parse(backendError);
        if (parsed?.detail && typeof parsed.detail === 'string') return parsed.detail;
        if (parsed?.message && typeof parsed.message === 'string') return parsed.message;
        if (parsed?.title && typeof parsed.title === 'string') return parsed.title;
      } catch {
        // plain text message from backend
        return backendError;
      }
    } else if (typeof backendError === 'object') {
      const anyError = backendError as { detail?: string; message?: string; title?: string };
      if (anyError.detail) return anyError.detail;
      if (anyError.message) return anyError.message;
      if (anyError.title) return anyError.title;
    }

    return null;
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.context?.get === undefined || request.context.get(BYPASS_ERROR_INTERCEPTOR)) {
      // Here we handle the case where we want to bypass the error interceptor
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse | Error) => {
        logErrorToSentry(error, request);

        if (error instanceof Error || (error as HttpErrorResponse).error instanceof ErrorEvent) {
          // Error to initiate the request
          this.notificationService.showError('Impossibile completare la richiesta');
          return throwError(() => error);
        }

        const httpError = error as HttpErrorResponse;
        const backendMessage = this.extractBackendMessage(httpError);

        if (httpError.status === 404) {
          this.notificationService.showError(backendMessage || 'Entità non trovata');
          return throwError(() => error);
        } else if (httpError.status === 400) {
          this.notificationService.showError(backendMessage || 'Richiesta malformata, controlla i dati');
          return throwError(() => error);
        } else if (httpError.status === 401) {
          //Here we return empty to prevent to show the error message when the user is redirected to the login page by the authentication interceptor
          return EMPTY;
        } else if (httpError.status === 403) {
          if (this.isCarrierDisabledError(httpError)) {
            this.authService.handleCarrierDisabled();
            return EMPTY;
          }
          this.notificationService.showError(backendMessage || (httpError.error as string) || 'Accesso negato');
          return throwError(() => error);
        }

        // If we have set the requestType we want a "more specific" generic error message
        const requestType = request.context.get(ERROR_INTERCEPTOR_REQUEST_TYPE);
        if (requestType && requestType !== 'generic') {
          let errorTranslationKey;

          if (requestType === 'create') errorTranslationKey = "Impossibile creare l'entità richiesta";
          else if (requestType === 'update') errorTranslationKey = "Impossibile aggiornare l'entità richiesta";
          else if (requestType === 'delete') errorTranslationKey = "Impossibile eliminare l'entità richiesta";
          else if (requestType === 'get') errorTranslationKey = "Impossibile recuperare l'entità richiesta";
          else errorTranslationKey = 'Errore interno del server';

          this.notificationService.showError(backendMessage || errorTranslationKey);
          return throwError(() => error);
        }

        // Here will be the default case, where all the "unhandled" backend errors will be caught
        this.notificationService.showError(backendMessage || 'Errore interno del server');
        return throwError(() => error);
      }),
    );
  }
}
